import { apiClient } from "../client";

export interface ProductCategory {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
}

/** Spatie media-library item attached to a product. */
export interface ProductMedia {
  id: number;
  original_url: string;
  file_name?: string;
  order_column?: number;
  custom_properties?: { is_main?: boolean };
}

/** Admin-entered spec rows shown on the product detail page. */
export interface ProductSpecification {
  id: number;
  title: string;
  description: string;
  sort_order: number;
}

/** Product record as returned by the live /products* endpoints. */
export interface Product {
  id: number;
  name: string;
  slug: string;
  product_type: string;
  /** Decimal strings, e.g. "10000.00". */
  price: string;
  /** Base price before discount (decimal string). */
  product_price: string;
  discount_rate: string;
  discount_price?: string | null;
  /** Final payable price incl. GST (decimal string). */
  total_price: string;
  final_price: string;
  gst_rate?: string;
  gst_amount?: string;
  stock: string | number;
  sku?: string | null;
  weight?: string | null;
  dimensions?: string | null;
  ratti?: string | null;
  carat?: string | null;
  origin_name?: string | null;
  variation_name?: string;
  is_wishlisted?: boolean;
  /** Short HTML blurb. */
  sort_description?: string | null;
  long_description?: string | null;
  is_featured?: boolean | number;
  is_special?: boolean | number;
  is_home?: boolean | number;
  image_url: string | null;
  categories?: ProductCategory[];
  media?: ProductMedia[];
  variations?: unknown[];
}

/** Detail payload — the base product plus reviews/specs/origin extras. */
export interface ProductDetails extends Product {
  reviews?: unknown[];
  specifications?: ProductSpecification[];
  origin_country?: { id: number; name: string } | null;
}

export interface RatingDistribution {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}

/**
 * /product/details/:slug is the one catalog route that does NOT use the
 * standard envelope — the payload lives under `product`, not `data`.
 * It also rides back everything the page needs in one call: related
 * products (keyed "1".."N", not an array — same Product shape as the base
 * product) and the review aggregate, so no follow-up requests are needed.
 */
export interface ProductDetailsResponse {
  status: boolean;
  message?: string;
  product: ProductDetails;
  relatedProducts?: Record<string, Product>;
  reviews?: unknown[];
  ratingDistribution?: RatingDistribution;
  averageRating?: number | null;
}

/** Standard Laravel length-aware paginator. */
export interface LaravelPaginator<T> {
  current_page: number;
  data: T[];
  last_page: number;
  per_page: number;
  total: number;
  next_page_url: string | null;
  prev_page_url: string | null;
}

export interface Envelope<T> {
  status: boolean;
  message?: string;
  data: T;
}

/** Slimmer product record returned by /product/search (differs from Product). */
export interface SearchProduct {
  id: number;
  name: string;
  slug: string;
  product_type: string;
  long_description: string | null;
  sort_description: string | null;
  /** Decimal string, e.g. "10000.00". */
  price: string;
  discount_rate: string | null;
  discount_amount: string;
  discount_price: string;
  has_discount: boolean;
  gst_rate: string | null;
  gst_amount: string;
  /** What the shop shows: price after discount, plus GST. */
  total_price: string;
  image_url: string | null;
  /** Primary category name. */
  category: string | null;
  /** Category names, not objects. */
  categories: string[];
  grade: string | null;
  product_grade_id: number | null;
  origin: string | null;
  origin_id: number | null;
  ratti: string | null;
  carat: string | null;
  brand: string | null;
  tags: string[];
  stock: string;
  in_stock: boolean;
  is_featured: boolean;
  is_special: boolean;
  /** Approved-review average, null when unreviewed. */
  average_rating: number | null;
  review_count: number;
  relevance: number;
}

interface FacetOption {
  id: number;
  name: string;
  count: number;
}

/** Filter-sidebar counts, returned when `include_facets=1`. Each group's
 * counts ignore that group's own selection (multi-select friendly). */
export interface SearchFacets {
  total: number;
  /** Shown-price (after discount + GST) bounds. */
  price: { min: number | null; max: number | null };
  ratti: { min: number | null; max: number | null };
  carat: { min: number | null; max: number | null };
  categories: (FacetOption & { slug: string; parent_id: number | null })[];
  grades: FacetOption[];
  origins: FacetOption[];
  product_types: { value: string; count: number }[];
  tags: FacetOption[];
  attributes: { id: number; name: string; slug: string; values: { id: number; value: string; count: number }[] }[];
  discounts: { min: number; count: number }[];
  ratings: { min: number; count: number }[];
  flags: { in_stock: number; on_sale: number; featured: number; special: number; home: number };
  sorts: ProductSearchSort[];
}

export type ProductSearchSort =
  | "relevance"
  | "price_low_high"
  | "price_high_low"
  | "newest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "discount_desc"
  | "rating_desc"
  | "popularity"
  | "ratti_asc"
  | "ratti_desc"
  | "carat_asc"
  | "carat_desc";

/** Multi-value filters accept an array or a comma-separated string. */
type IdList = number | string | (number | string)[];
type Flag = 0 | 1;

/** Laravel API Resource collection — top-level data/links/meta, no envelope. */
export interface ResourcePaginator<T> {
  data: T[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number | null;
    last_page: number;
    path: string;
    per_page: number;
    to: number | null;
    total: number;
  };
}

export interface ProductSearchParams {
  /** Free-text query over name, descriptions and SKU, e.g. "Sapphire". */
  q?: string;
  /** Category ids; a parent category includes its subcategories. */
  category_id?: IdList;
  /** Category slugs — alternative to category_id. */
  category?: string;
  /** Numeric grade ids from GET /product-grades. */
  product_grade_id?: IdList;
  /** Grade names, case-insensitive, e.g. "A,B+". */
  grade?: string;
  /** Origin country ids. */
  origin_id?: IdList;
  /** Origin country names, case-insensitive. */
  origin?: string;
  /** "gems" | "simple" | "attribute", comma-separated for several. */
  product_type?: string;
  tag_id?: IdList;
  tag?: string;
  /** Filter-attribute value ids: OR within an attribute, AND across them. */
  attribute_value_id?: IdList;
  ratti?: number | string;
  carat?: number | string;
  min_ratti?: number | string;
  max_ratti?: number | string;
  min_carat?: number | string;
  max_carat?: number | string;
  /** Bounds on the shown price (after discount + GST). */
  min_price?: number | string;
  max_price?: number | string;
  /** Send 1 to restrict to in-stock products; omit otherwise. */
  in_stock?: Flag;
  on_sale?: Flag;
  /** Minimum discount percent. */
  min_discount?: number;
  is_featured?: Flag;
  is_special?: Flag;
  /** Minimum average approved-review rating (1-5). */
  min_rating?: number;
  sort?: ProductSearchSort;
  /** Send 1 to get `facets` alongside the page. */
  include_facets?: Flag;
  page?: number;
  per_page?: number;
}

/** Gemstone quality grade row from GET /product-grades. */
export interface ProductGrade {
  id: number;
  name: string;
  [key: string]: unknown;
}

export const catalog = {
  /** Full product listing — paginated. */
  list: (params: { page?: number; per_page?: number } = {}) =>
    apiClient.get<Envelope<LaravelPaginator<Product>>>("/products", { params: { ...params } }),

  /** Featured products — paginated, 8 per page by default upstream. */
  featured: (params: { page?: number } = {}) =>
    apiClient.get<Envelope<LaravelPaginator<Product>>>("/featured-products", {
      params: { ...params },
    }),

  /** Single product by numeric id. */
  get: (id: number) => apiClient.get<Envelope<Product>>(`/products/${id}`),

  /** Product details by slug (the shop's canonical detail read). */
  bySlug: (slug: string) => apiClient.get<ProductDetailsResponse>(`/product/details/${slug}`),

  /** Product search — paginated, with advanced filters, sorts and optional facets. */
  search: (params: ProductSearchParams = {}) =>
    apiClient.get<ResourcePaginator<SearchProduct> & { facets?: SearchFacets }>("/product/search", {
      // Arrays go out comma-joined: axios's default `key[]=` form isn't parsed
      // as a list by every proxy hop.
      params: Object.fromEntries(
        Object.entries(params).map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v]),
      ),
    }),

  /** best selling products */
  bestSelling: () => apiClient.get<Envelope<Product[]>>("/best-selling-products"),

  /** Category tree for navigation/filters — optionally scoped to one parent. */
  categories: (parent_slug?: string) =>
    apiClient.get<Envelope<ProductCategory[]>>("/categories", {
      params: parent_slug ? { parent_slug } : undefined,
    }),

  /** Gemstone quality grades — reference list for the search filter. */
  productGrades: () =>
    apiClient.get<Envelope<ProductGrade[]> | ProductGrade[]>("/product-grades"),

  /** category wise products */
  categoryWiseProducts: (slug: string,) =>
    apiClient.get<Envelope<ProductCategory[]>>(`/categories-wise-products/${slug}`),
};
